/**
 * Athlete Coaching Home — Stage 9/10 remain read-only. Goal intake and
 * training-plan generation are explicit athlete-initiated write surfaces
 * that call Chart 1 / AI Coach APIs only; they do not turn the rest of
 * the coaching context into an unrestricted editor.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertCircle, RefreshCw, ClipboardList } from "lucide-react";

import { CoachingHomeSkeleton }
  from "@/components/coaching/CoachingSkeleton";
import { CurrentWeekCard }
  from "@/components/coaching/CurrentWeekCard";
import { MissingInformationCard }
  from "@/components/coaching/MissingInformationCard";
import {
  PlanChangeCard,
  isActionablePlanSuggestion,
} from "@/components/coaching/PlanChangeCard";
import { RecentSessionCard }
  from "@/components/coaching/RecentSessionCard";
import { SafetyBanner }
  from "@/components/coaching/SafetyBanner";
import { TodayCoachingCard }
  from "@/components/coaching/TodayCoachingCard";
import { WorkoutFocusCard }
  from "@/components/coaching/WorkoutFocusCard";
import { WorkoutPurposeCard }
  from "@/components/coaching/WorkoutPurposeCard";
import { ActiveTrainingPlanCard }
  from "@/components/coaching/ActiveTrainingPlanCard";
import { AdaptiveTrainingCard }
  from "@/components/coaching/AdaptiveTrainingCard";
import { PlannedSessionEditor }
  from "@/components/coaching/PlannedSessionEditor";
import {
  fetchCurrentActiveTrainingPlan,
  fetchCurrentTrainingPlanLifecycle,
} from "@/api/trainingPlanApi";
import { AskYourCoachPanel }
  from "@/components/coaching/AskYourCoachPanel";
import { TrainingGoalSetup }
  from "@/components/coaching/TrainingGoalSetup";
import { TrainingPlanSetup }
  from "@/components/coaching/TrainingPlanSetup";
import { ActivePlanSafetyBanner }
  from "@/components/coaching/ActivePlanSafetyBanner";
import { useCoachingContext } from "@/hooks/useCoachingContext";
import { useCoachingExplanationBundle }
  from "@/hooks/useCoachingExplanation";
import { PageHeader } from "@/components/PageHeader";
import { CoachPendingActionNotice }
  from "@/components/coaching/CoachPendingActionNotice";
import { TRAINING_PLAN_DRAFT_READY_EVENT } from "@/lib/coachingActionGuard";
import { useCoachPendingAction } from "@/hooks/useCoachPendingAction";
import { ACTIVE_PLAN_CHANGED_EVENT } from "@/lib/activePlanSync";

const sectionError = (result) => {
  if (!result || !result.isError) return null;
  const err = result.error;
  return err?.coachingError
    || { code: "unknown",
          message: "Section temporarily unavailable" };
};

export const CoachingHome = () => {
  const location = useLocation();
  const contextQuery = useCoachingContext();
  const { primary: coachPendingAction } = useCoachPendingAction();
  const bundle = useCoachingExplanationBundle({
    detailLevel: "STANDARD",
  });
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupMode, setSetupMode] = useState(null);
  // Holds the VALIDATED_DRAFT payload returned by /generate-training-plan
  // so TrainingPlanDraftPanel can hydrate immediately, without a page
  // refresh. Reset when the panel finishes acting on it or the user
  // reopens setup.
  const [generatedDraft, setGeneratedDraft] = useState(null);
  const [creatorFocus, setCreatorFocus] = useState(0);
  const [activePlan, setActivePlan] = useState(null);
  const [editingSession, setEditingSession] = useState(null);

  const reloadActivePlan = () =>
    fetchCurrentActiveTrainingPlan()
      .then((plan) => setActivePlan(plan || null))
      .catch(() => setActivePlan(null));

  const refetchCoachingContext = contextQuery.refetch;

  useEffect(() => {
    reloadActivePlan();
    const onPlanChanged = () => {
      void Promise.all([
        reloadActivePlan(),
        refetchCoachingContext?.(),
      ]);
    };
    window.addEventListener(ACTIVE_PLAN_CHANGED_EVENT, onPlanChanged);
    return () => window.removeEventListener(ACTIVE_PLAN_CHANGED_EVENT, onPlanChanged);
  }, [refetchCoachingContext]);

  useEffect(() => {
    if (contextQuery.isLoading) return undefined;
    const hash = location.hash?.replace("#", "");
    if (!hash) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [contextQuery.isLoading, location.hash]);

  useEffect(() => {
    const onDraftReady = (event) => {
      const draft = event?.detail;
      if (draft?.status === "VALIDATED_DRAFT") {
        setGeneratedDraft(draft);
      }
    };
    window.addEventListener(TRAINING_PLAN_DRAFT_READY_EVENT, onDraftReady);
    return () => window.removeEventListener(TRAINING_PLAN_DRAFT_READY_EVENT, onDraftReady);
  }, []);

  const openTrainingPlanSetup = (mode = null) => {
    setSetupMode(mode);
    setSetupOpen(true);
  };

  const openTrainingPlanCreator = () => {
    setCreatorFocus((current) => current + 1);
  };

  if (contextQuery.isLoading) {
    return (
      <div className="max-w-6xl mx-auto min-w-0" data-testid="coaching-loading">
        <PageHeader
          title="AI Coach"
          subtitle="Loading your coaching context…"
        />
        <CoachingHomeSkeleton />
      </div>
    );
  }

  if (contextQuery.isError) {
    const err = contextQuery.error?.coachingError
      || { code: "unknown",
            message: "Coaching is temporarily unavailable" };
    return (
      <div className="max-w-6xl mx-auto min-w-0" data-testid="coaching-full-error">
        <PageHeader
          eyebrow="Coach"
          title="AI Coach"
          subtitle="Coaching is temporarily unavailable."
        />
        <div
          role="alert"
          className="app-card border-analytics-red/40 text-analytics-red bg-analytics-red/10 p-6"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <h2 className="font-heading text-xl tracking-wider uppercase">
                Coach unavailable
              </h2>
              <p className="mt-2 text-sm">{err.message}</p>
              <button
                type="button"
                onClick={() => contextQuery.refetch()}
                className="btn-secondary-app mt-4 px-6 py-3 text-xs tracking-[0.15em] uppercase min-h-[44px]"
                data-testid="coaching-retry"
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const ctx = contextQuery.data || {};
  const timezone = ctx.timezone;
  const contextStatus = ctx.context_status;
  const safety = ctx.safety_and_reassessment;
  const reassessmentRequired =
    safety?.reassessment_required
    || contextStatus === "CONTEXT_REASSESSMENT_REQUIRED";
  const safetyBlocked = safety?.training_suspended
    || contextStatus === "CONTEXT_BLOCKED_FOR_SAFETY";
  const trainingSuppressed = safetyBlocked;

  const isInconsistent = contextStatus === "CONTEXT_INCONSISTENT";

  const todaySession = (ctx.today?.scheduled_sessions || [])[0];

  return (
    <div className="max-w-6xl mx-auto space-y-6 min-w-0"
            data-testid="coaching-home">
      <ActivePlanSafetyBanner />
      <PageHeader
        eyebrow="Coach"
        title="AI Coach"
        subtitle="Create or change your plan in six short steps. One-time setup is optional. Coaching cards stay read-only."
      />

      {coachPendingAction && (
        <CoachPendingActionNotice
          primary={coachPendingAction}
          testId="coaching-home-pending-action"
        />
      )}

      <TrainingGoalSetup
        focusToken={creatorFocus}
        incomingDraft={generatedDraft}
        onIncomingDraftConsumed={() => setGeneratedDraft(null)}
        loadCurrentLifecycle={fetchCurrentTrainingPlanLifecycle}
        onPlanGenerated={(result) => {
          if (result?.completed) {
            setGeneratedDraft(null);
            reloadActivePlan();
            contextQuery.refetch?.();
          }
        }}
      />

      <div id="coaching-suggestion-slot" data-testid="coaching-suggestion-slot" />

      <PlanChangeCard
        variant="suggestion"
        adaptation={ctx.adaptation_summary}
        timezone={timezone}
        explanation={bundle.planChange.data}
        error={sectionError(bundle.planChange)}
        onResolved={() => contextQuery.refetch()}
      />

      {isInconsistent && (
        <div role="alert"
                data-testid="coaching-inconsistent-banner"
                className="app-card border-analytics-amber/40 text-analytics-amber bg-analytics-amber/10 p-4 text-sm">
          Your coaching context is temporarily inconsistent. Explanations
          may be partial while this resolves.
        </div>
      )}

      <SafetyBanner
        safety={safety}
        safetyExplanation={bundle.safety.data}
        reassessmentRequired={reassessmentRequired}
      />

      <div className="space-y-6">

      <TrainingPlanSetup
        open={setupOpen}
        onOpenChange={(next) => {
          setSetupOpen(next);
          if (!next) setSetupMode(null);
        }}
        forcedMode={setupMode}
        onGenerated={() => {
          contextQuery.refetch?.();
        }}
      />

      <section className="space-y-4" aria-label="Daily coaching">

      <div id="active-training-plan" className="scroll-mt-24">
      <ActiveTrainingPlanCard
        block={ctx.current_training_block}
        goal={ctx.goal}
        athlete={ctx.athlete}
        week={ctx.current_week}
        today={ctx.today}
        upcoming={ctx.upcoming_training || []}
        compliance={ctx.compliance_summary}
        timezone={timezone}
        activePlan={activePlan}
        onChangePlan={openTrainingPlanCreator}
        onEditSession={setEditingSession}
      />
      </div>

      <TodayCoachingCard
        today={ctx.today}
        timezone={timezone}
        explanation={bundle.today.data}
        trainingSuppressed={trainingSuppressed}
        activePlan={activePlan}
      />

      <AdaptiveTrainingCard timezone={timezone} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WorkoutPurposeCard
          explanation={bundle.purpose.data}
          error={sectionError(bundle.purpose)}
        />
        <WorkoutFocusCard
          explanation={bundle.focus.data}
          todaySession={todaySession}
          error={sectionError(bundle.focus)}
        />
      </div>
      </section>

      <section className="space-y-4" aria-label="Training context">

      <CurrentWeekCard
        week={ctx.current_week}
        compliance={ctx.compliance_summary}
        timezone={timezone}
        complianceExplanation={bundle.weeklyCompliance.data}
        error={sectionError(bundle.weeklyCompliance)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentSessionCard
          recent={(ctx.recent_completed_training || [])[0]}
          timezone={timezone}
          explanation={bundle.lastSession.data}
          error={sectionError(bundle.lastSession)}
        />
        <div id="plan-change">
          <PlanChangeCard
            adaptation={ctx.adaptation_summary}
            timezone={timezone}
            explanation={bundle.planChange.data}
            error={sectionError(bundle.planChange)}
            onResolved={() => contextQuery.refetch()}
            suppressActions={isActionablePlanSuggestion(ctx.adaptation_summary)}
          />
        </div>
      </div>
      </section>

      <section className="space-y-4" aria-label="Coach conversation">
      <AskYourCoachPanel
        onRequestTrainingIntake={(payload) => {
          openTrainingPlanSetup(payload?.mode || null);
        }}
        onReviewTrainingPlanDraft={(draft) => {
          if (draft?.raw?.status === "VALIDATED_DRAFT") {
            setGeneratedDraft(draft.raw);
            openTrainingPlanCreator();
          }
        }}
      />

      <div
        className="app-card-elevated flex flex-col gap-4 p-5 sm:p-6 sm:flex-row sm:items-center sm:justify-between"
        data-testid="coaching-home-primary-cta"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="icon-frame w-10 h-10 shrink-0"
            style={{
              color: "var(--brand-primary)",
              background: "rgb(49 185 120 / 0.12)",
              borderColor: "rgb(49 185 120 / 0.3)",
            }}
          >
            <ClipboardList className="w-5 h-5" strokeWidth={1.75} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="section-title text-base sm:text-lg">
              Setup My Training Plan
            </h2>
            <p className="meta-text mt-1 leading-relaxed">
              One-time medical, equipment, and preference information.
              Optional — use the steps at the top to create or change your plan.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => openTrainingPlanSetup(null)}
          data-testid="open-training-plan-setup"
          className="btn-secondary-app px-5 py-2.5 text-xs tracking-[0.2em] uppercase shrink-0 w-full sm:w-auto"
        >
          Set up my training plan
        </button>
      </div>

      <MissingInformationCard
        items={ctx.missing_information}
        error={sectionError(bundle.missing)}
      />
      </section>
      {editingSession && (
        <PlannedSessionEditor
          session={editingSession}
          onClose={() => setEditingSession(null)}
          onSaved={() => {
            setEditingSession(null);
            reloadActivePlan();
          }}
        />
      )}
      </div>
    </div>
  );
};

export default CoachingHome;
